import SearchAndFilterBar from "../../components/SearchAndFilterBar";
import Loading from "../../components/Loading";
import PieChart from "../../components/PieChart";
import { EmptyChart, hasChartData } from "../../components/EmptyChart";
import Button from "../../components/Button";

const OverviewTabPresenter = ({
    loading,
    exporting,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    handleApplyFilter,
    handleExport,
    serviceReport,
    profileReport,
    financialReport,
    hoveredCard,
    setHoveredCard,
    navigate
}) => {
    if (loading || exporting) return <Loading />;

    return (
        <div className="flex flex-col gap-6 animate-in fade-in duration-500">
            <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 bg-white p-4 sm:p-6 border border-[#D2D5DA] rounded-[24px] shadow-sm">
                <div className="hidden lg:block">
                    <h2 className="text-xl font-bold text-gray-800">Tổng quan báo cáo</h2>
                    <p className="text-sm text-gray-500">Xem tóm tắt thông tin dịch vụ, hồ sơ và tài chính</p>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full lg:w-auto">
                    <SearchAndFilterBar
                        showSearch={false}
                        showAddButton={false}
                        showDateRangePickers
                        fromDateValue={fromDate}
                        toDateValue={toDate}
                        onFromDateChange={setFromDate}
                        onToDateChange={setToDate}
                        filterButton={true}
                        filterButtonVariant="primary"
                        filterLabel="Áp dụng"
                        onFilterClick={handleApplyFilter}
                    />
                    <Button
                        type="button"
                        variant="secondary"
                        size="lg"
                        onClick={handleExport}
                        disabled={exporting}
                        isLoading={exporting}
                        className="w-full sm:w-auto"
                        rightIcon={
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                <polyline points="7 10 12 15 17 10" />
                                <line x1="12" y1="15" x2="12" y2="3" />
                            </svg>
                        }
                    >
                        {exporting ? "Đang xuất..." : "Xuất báo cáo PDF"}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-6">
                <OverviewReportCard
                    title="Báo cáo dịch vụ"
                    subtitle="Thống kê theo loại dịch vụ"
                    data={serviceReport?.chart}
                    onClick={() => navigate("/bao-cao-thong-ke/dich-vu")}
                    isHovered={hoveredCard === "service"}
                    onMouseEnter={() => setHoveredCard("service")}
                    onMouseLeave={() => setHoveredCard(null)}
                />
                <OverviewReportCard
                    title="Báo cáo hồ sơ"
                    subtitle="Trạng thái xử lý hồ sơ"
                    data={profileReport?.chart}
                    onClick={() => navigate("/bao-cao-thong-ke/ho-so")}
                    isHovered={hoveredCard === "profile"}
                    onMouseEnter={() => setHoveredCard("profile")}
                    onMouseLeave={() => setHoveredCard(null)}
                />
                <OverviewReportCard
                    title="Báo cáo tài chính"
                    subtitle="Doanh thu và thanh toán"
                    data={financialReport?.chart}
                    onClick={() => navigate("/bao-cao-thong-ke/tai-chinh")}
                    isHovered={hoveredCard === "finance"}
                    onMouseEnter={() => setHoveredCard("finance")}
                    onMouseLeave={() => setHoveredCard(null)}
                    totalFormatter={(v) => v.toLocaleString() + " ₫"}
                    centerSubLabel="Tổng tiền"
                />
            </div>
        </div>
    );
};

const OverviewReportCard = ({
    title,
    subtitle,
    data,
    onClick,
    isHovered,
    onMouseEnter,
    onMouseLeave,
    totalFormatter,
    centerSubLabel
}) => (
    <div
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className="w-full rounded-[24px] p-6 lg:p-8 flex flex-col gap-6 cursor-pointer transition-all duration-500 bg-white border border-[#D2D5DA] shadow-sm hover:shadow-2xl hover:-translate-y-1 group relative overflow-hidden"
    >
        <div className={`absolute top-0 right-0 w-32 h-32 -mr-16 -mt-16 rounded-full transition-all duration-700 ${isHovered ? "bg-blue-500/10 scale-150" : "bg-gray-50/50 scale-100"}`} />

        <div className="relative">
            <h3 className="text-xl font-bold text-gray-800 transition-colors group-hover:text-[#0457C2]">{title}</h3>
            <p className="text-sm text-gray-500 mt-1">{subtitle}</p>
        </div>

        <div className={`relative rounded-[24px] p-4 flex items-center justify-center transition-all duration-500 ${isHovered ? "bg-blue-50/50" : "bg-gray-50"}`}>
            {hasChartData(data) ? (
                <div className="w-full max-w-[260px]">
                    <PieChart
                        data={data}
                        size={220}
                        legendPosition="bottom"
                        totalFormatter={totalFormatter}
                        centerSubLabel={centerSubLabel}
                    />
                </div>
            ) : (
                <div className="h-[260px] flex items-center justify-center w-full">
                    <EmptyChart title={`Chưa có dữ liệu ${title.toLowerCase()}`} />
                </div>
            )}
        </div>

        <div className="flex justify-between items-center gap-4 mt-2">
            <span className="text-sm font-semibold text-gray-400 group-hover:text-[#0457C2] transition-colors">Xem chi tiết</span>
            <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 shadow-md ${isHovered ? "bg-[#0457C2] text-white" : "bg-gray-100 text-gray-400"}`}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                </svg>
            </div>
        </div>
    </div>
);

export default OverviewTabPresenter;
